<template>
  <v-app>
    <vertical-nav-menu :is-drawer-open.sync="isDrawerOpen"></vertical-nav-menu>

    <v-app-bar app flat absolute color="transparent">
      <div class="boxed-container w-full">
        <div class="d-flex align-center mx-6">
          <!-- Hamburger — desktop only -->
          <v-app-bar-nav-icon
            class="d-none d-lg-block me-2"
            @click="isDrawerOpen = !isDrawerOpen"
          ></v-app-bar-nav-icon>

          <v-spacer></v-spacer>
          <theme-switcher></theme-switcher>

          <app-locale></app-locale>

          <!-- Bell notification pill -->
          <v-menu
            offset-y left nudge-bottom="14" min-width="240"
            content-class="user-profile-menu-content" z-index="1000"
          >
            <template v-slot:activator="{ on, attrs }">
              <button class="appbar-pill bell-pill ms-3" v-bind="attrs" v-on="on" :title="$t('navbar.notifications')">
                <span v-if="notifications.lastNotificationsCount" class="bell-badge">
                  {{ notifications.lastNotificationsCount > 99 ? '99+' : notifications.lastNotificationsCount }}
                </span>
                <v-icon size="18" :color="notifications.lastNotificationsCount ? '#e65100' : '#546E7A'">
                  {{ icons.mdiBellOutline }}
                </v-icon>
              </button>
            </template>
            <v-list v-if="notifications.lastNotifications.length > 0">
              <v-list-item
                link
                v-for="notification in notifications.lastNotifications"
                :key="notification.id"
                @click="viewAllNotifications"
              >
                <v-list-item-content>
                  <v-list-item-title class="notification-item">
                    <span v-if="notification.data.title">{{ notification.data.title }}</span>
                    <span v-else-if="notification.data.student && notification.data.student.name">{{ notification.data.student.name }}</span>
                    -
                    <span class="notification-item-message">{{ displayNotificationType(notification) }}</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item link @click="viewAllNotifications">
                <v-list-item-content>
                  <v-list-item-title class="text-center">{{ $t('navbar.all_notifications') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-list v-else>
              <v-list-item>
                <v-list-item-title>{{ $t('navbar.no_notifications') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <app-bar-user-menu
            :admin-name="adminProfileStore.name"
            :admin-avatar="adminProfileStore.avatar"
            :admin-organisation-name="adminProfileStore.organisation_name"
            :profile-url="'/student/view-profile'"
            :admin="false"
          ></app-bar-user-menu>
        </div>
      </div>
    </v-app-bar>

    <v-main>
      <div class="app-content-container boxed-container pa-6">
        <slot></slot>
      </div>
    </v-main>

    <!-- Mobile bottom navigation (xs/sm only) — 3 items -->
    <div v-if="$vuetify.breakpoint.smAndDown" class="sbn-bar">
      <router-link :to="{ name: 'student-live-tracking' }" class="sbn-item" active-class="sbn-item--active">
        <div class="sbn-icon-wrap sbn-orange">
          <v-icon size="22" color="#e65100">mdi-crosshairs-gps</v-icon>
        </div>
        <span class="sbn-label">Live</span>
      </router-link>
      <router-link :to="{ name: 'student-payments' }" class="sbn-item" active-class="sbn-item--active">
        <div class="sbn-icon-wrap sbn-green">
          <v-icon size="22" color="#388E3C">mdi-credit-card-multiple-outline</v-icon>
        </div>
        <span class="sbn-label">Pagesa</span>
      </router-link>
      <router-link :to="{ name: 'student-notifications' }" class="sbn-item" active-class="sbn-item--active">
        <div class="sbn-icon-wrap sbn-purple">
          <v-icon size="22" color="#7B1FA2">mdi-bell-outline</v-icon>
        </div>
        <span class="sbn-label">Njoftimet</span>
      </router-link>
    </div>

    <install-prompt></install-prompt>
  </v-app>
</template>

<script>
import StudentVerticalNavMenu from './components/vertical-nav-menu/StudentVerticalNavMenu.vue';
import ThemeSwitcher from '@/layouts/components/ThemeSwitcher.vue';
import AppBarUserMenu from '@/layouts/components/AppBarUserMenu.vue';
import { activationStore, adminProfileStore } from '@/utils/helpers';
import { mdiBellOutline, mdiGithub, mdiMagnify } from '@mdi/js';
import auth from '@/services/AuthService';
import Pusher from 'pusher-js';
import Echo from 'laravel-echo';
import { Keys } from '@/config';
import AppLocale from './components/AppLocale/AppLocale.vue';
import InstallPrompt from './components/InstallPrompt.vue';
import notificationService from '@/services/NotificationService';
import { messaging } from '@/firebaseConfig';

export default {
  components: {
    InstallPrompt,
    VerticalNavMenu: StudentVerticalNavMenu,
    ThemeSwitcher,
    AppBarUserMenu,
    AppLocale,
  },
  setup() {
    return { adminProfileStore, activationStore };
  },
  data() {
    return {
      mode: null,
      secureKey: null,
      isDrawerOpen: true,
      icons: { mdiMagnify, mdiBellOutline, mdiGithub },
      notifications: notificationService.state,
    };
  },
  created() {
    if (this.$vuetify.breakpoint.mdAndDown) {
      this.isDrawerOpen = false;
    }
    this.initPusher();
  },
  mounted() {
    this.mode = auth.getMode();
    notificationService.loadLastNotifications();
    this.loadNotifications();
    this.requestPermission();
  },
  methods: {
    loadNotifications() {
      axios.get('/notifications/all').then((response) => {
        this.adminProfileStore.name = response.data.adminName;
        this.adminProfileStore.avatar = response.data.adminAvatar;
        this.adminProfileStore.id = response.data.adminId;
        this.adminProfileStore.organisation_name = response.data?.adminOrganisation?.name;
        this.secureKey = response.data.secureKey;
        this.activationStore.isActivated = true;

        if (this.adminProfileStore?.id) {
          window.Echo.private(
            `App.Models.User.${this.adminProfileStore.id}`
          ).notification(() => {
            notificationService.loadLastNotifications();
          });
        }
      });
    },
    initPusher() {
      window.Pusher = Pusher;
      const accessToken = localStorage.getItem('freshToken');
      if (!accessToken) return;
      window.Echo = new Echo({
        broadcaster: 'reverb',
        key: Keys.VUE_APP_REVERB_APP_KEY,
        wsHost: Keys.VUE_APP_REVERB_HOST,
        wsPort: Keys.VUE_APP_REVERB_PORT ?? 9090,
        wssPort: Keys.VUE_APP_REVERB_PORT ?? 443,
        forceTLS: (Keys.VUE_APP_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
        authEndpoint: `${Keys.VUE_APP_REVERB_SCHEME ?? 'https'}://${Keys.VUE_APP_REVERB_HOST}/broadcasting/auth`,
        auth: { headers: { Authorization: 'Bearer ' + accessToken } },
      });
    },
    viewAllNotifications() {
      this.$router.push({ name: 'student-notifications' });
    },
    displayNotificationType(notification) {
      if (notification?.type === 'App\\Notifications\\StudentTaken') {
        return this.$t('navbar.notification_taken');
      } else if (notification?.type === 'App\\Notifications\\StudentDropped') {
        return this.$t('navbar.notification_dropped');
      } else if (notification?.type === 'App\\Notifications\\MessageNotification') {
        return notification?.data?.message;
      } else if (notification?.type === 'App\\Notifications\\PaymentNotification') {
        return this.$t('navbar.notification_paid', { months: notification?.data?.payment?.months?.join(', ') });
      }
      return '-';
    },
    async requestPermission() {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          messaging.getToken({
            vapidKey: 'BCpFjiClOtuSlHXDHWWI0ZPoyRHGJHumPZ7Ly5cKpDh1qgN4d9hIgwT-HeJgr_eAkT0RS_WG3VVO4o_GV_th1yw',
          }).then((currentToken) => {
            if (currentToken) {
              axios.post('/notifications/store-token', { fcm_token: currentToken });
            }
          }).catch((err) => {
            console.error('Error retrieving token:', err);
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.v-app-bar ::v-deep {
  .v-toolbar__content {
    padding: 0;
  }
}
.boxed-container {
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
}
.notification-item {
  display: flex;
  align-content: center;
  gap: 4px;
}
.notification-item-message {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

/* Push page content above bottom nav on mobile */
@media (max-width: 959px) {
  .v-main .app-content-container {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
  }
}

/* ── Bottom navigation bar ── */
.sbn-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  height: calc(68px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid rgba(0,0,0,0.08);
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  z-index: 100;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
  padding: 0 8px env(safe-area-inset-bottom, 0px);
}
.sbn-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  flex: 1;
  padding: 6px 4px;
  border-radius: 12px;
  transition: background 0.15s;
}
.sbn-item:active { background: rgba(0,0,0,0.04); }
.sbn-icon-wrap {
  width: 40px; height: 40px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.15s;
}
.sbn-item--active .sbn-icon-wrap { transform: translateY(-2px); }

.sbn-orange { background: rgba(230,81,0,0.12); }
.sbn-green  { background: rgba(56,142,60,0.12); }
.sbn-purple { background: rgba(123,31,162,0.12); }

.sbn-item--active .sbn-orange { background: rgba(230,81,0,0.22); }
.sbn-item--active .sbn-green  { background: rgba(56,142,60,0.22); }
.sbn-item--active .sbn-purple { background: rgba(123,31,162,0.22); }

.sbn-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  transition: color 0.15s;
}
.sbn-item--active .sbn-label { color: #1a2744; }

/* Bell pill */
.bell-pill { position: relative; }
.bell-badge {
  position: absolute;
  top: -5px; right: -5px;
  background: #E53935;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 9px; font-weight: 800;
  border-radius: 8px;
  min-width: 16px; height: 16px;
  padding: 0 3px; line-height: 16px;
  text-align: center;
  pointer-events: none;
  z-index: 1;
}
.theme--dark .bell-badge { box-shadow: 0 0 0 2px #1e1e1e; }
</style>
